import { useState, useEffect } from 'react';
import { loadReCAPTCHAv3 } from '../../utils/loadRecaptcha';
import { submitSupplierForm } from '../../services/api.service';

const SITE_KEY = process.env.REACT_APP_SITE_KEY;
const SupplierForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    workingYears: '',
    address: '',
    city: '',
    pinCode: '',
    state: '',
    contactNumber: '',
    email: '',
    productCategory: '',
    productDetails: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [captchaToken, setCaptchaToken] = useState(null);

  useEffect(() => {
    loadReCAPTCHAv3(SITE_KEY).catch(err => {
      console.error('reCAPTCHA load error:', err);
    });
  }, []);


  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach(key => {
      if (!formData[key].trim()) {
        newErrors[key] = 'This field is required';
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.contactNumber && !/^\d{10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Please enter a valid 10-digit number';
    }

    if (formData.pinCode && !/^\d{6}$/.test(formData.pinCode)) {
      newErrors.pinCode = 'Please enter a valid 6-digit pincode';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) throw new Error('reCAPTCHA not loaded');

      const token = await grecaptcha.execute(SITE_KEY, { action: 'submit' });
      sessionStorage.setItem('captchaToken', token);
       await submitSupplierForm({
        ...formData,
        captchaToken: token
      });

      setSubmitStatus({ success: true, message: 'Form submitted successfully! We will contact you soon.' });
      setFormData({
        fullName: '',
        companyName: '',
        workingYears: '',
        address: '',
        city: '',
        pinCode: '',
        state: '',
        contactNumber: '',
        email: '',
        productCategory: '',
        productDetails: '',
        description: ''
      });
    } catch (err) {
      console.error(err);
      setSubmitStatus({ success: false, message: 'Submission failed. Try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    sessionStorage.setItem('captchaToken', token);
    if (errors.captcha) {
      setErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  const inputClasses = (fieldName) =>
    `w-full p-3 rounded-md bg-gray-100 border-0 ${errors[fieldName] ? 'ring-2 ring-red-500' : ''}`;

  return (
    <div className="md:w-1/2">
      <h3 className="text-2xl font-semibold mb-6">Feel free to reach out for a synergistic collaboration.</h3>

      {submitStatus.message && (
        <div className={`mb-6 p-4 rounded-md ${submitStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {submitStatus.message}
        </div>
      )}

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter Your Full Name *"
            className={inputClasses('fullName')}
          />
          {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
        </div>

        <div>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Company Name *"
            className={inputClasses('companyName')}
          />
          {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
        </div>

        <div>
          <input
            type="number"
            name="workingYears"
            value={formData.workingYears}
            onChange={handleChange}
            placeholder="No of Working Years *"
            className={inputClasses('workingYears')}
          />
          {errors.workingYears && <p className="text-red-500 text-sm mt-1">{errors.workingYears}</p>}
        </div>

        <div>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Address *"
            className={`${inputClasses('address')} h-32`}
          />
          {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
        </div>

        <div>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City *"
            className={inputClasses('city')}
          />
          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
        </div>

        <div>
          <input
            type="text"
            name="pinCode"
            value={formData.pinCode}
            onChange={handleChange}
            placeholder="PinCode *"
            className={inputClasses('pinCode')}
          />
          {errors.pinCode && <p className="text-red-500 text-sm mt-1">{errors.pinCode}</p>}
        </div>

        <div>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            placeholder="State *"
            className={inputClasses('state')}
          />
          {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
        </div>

        <div>
          <input
            type="tel"
            name="contactNumber"
            value={formData.contactNumber}
            onChange={handleChange}
            placeholder="Contact Number *"
            className={inputClasses('contactNumber')}
          />
          {errors.contactNumber && <p className="text-red-500 text-sm mt-1">{errors.contactNumber}</p>}
        </div>

        <div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="E-Mail *"
            className={inputClasses('email')}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <input
            type="text"
            name="productCategory"
            value={formData.productCategory}
            onChange={handleChange}
            placeholder="Product Category *"
            className={inputClasses('productCategory')}
          />
          {errors.productCategory && <p className="text-red-500 text-sm mt-1">{errors.productCategory}</p>}
        </div>

        <div>
          <textarea
            name="productDetails"
            value={formData.productDetails}
            onChange={handleChange}
            placeholder="Product Details *"
            className={`${inputClasses('productDetails')} h-32`}
          />
          {errors.productDetails && <p className="text-red-500 text-sm mt-1">{errors.productDetails}</p>}
        </div>

        <div>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description *"
            className={`${inputClasses('description')} h-32`}
          />
          {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
        </div>


        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3 rounded-md text-white ${isSubmitting
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-500 hover:bg-green-600'
            }`}
        >
          {isSubmitting ? 'Submitting...' : 'SUBMIT'}
        </button>
      </form>
    </div>
  );
};

export default SupplierForm;